import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { HashNavigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import Icon, { Icons } from '../../../../components/Icon/Icon';
import { t } from '../../../../lang';
import HelperService from '../../../../services/HelperService';
import SankeyDiagram from '../SankeyDiagram/SankeyDiagram';
import './AMLInsightBlock.scss';

const peekRationSlide = 0.06;
const spaceBetweenSlides = 14;
const widthBlockPercent = 48;
const iconSize = 16;

const colorParams = {
	green: { title: 'Low' },
	orange: { title: 'Medium' },
	red: { title: 'High' },
	violet: { title: 'Settle' },
	blue: { title: 'Uncategorized' },
};

const AMLInsightBlock = ({ details }) => {
	const ref = useRef(null);

	const [sentSignals, setSentSignals] = useState([]);
	const [receivedSignals, setReceivedSignals] = useState([]);
	const [uniqColorSignals, setUniqColorSignals] = useState([]);
	const [colorsPositionsLeft, setColorsPositionsLeft] = useState([]);
	const [colorsPositionsRight, setColorsPositionsRight] = useState([]);
	const [widthDiagram, setWidthDiagram] = useState(null);
	const [activeSlide, setActiveSlide] = useState(0);

	const [tooltipSwipe, setTooltipSwipe] = useState(true);

	const prepareDiagramPercentsBlocks = colorsPositionsArr => {
		const divHeight = 10; // px
		const correctionForSmallFibers = 4;
		let correction = 0;

		return colorsPositionsArr.map(({ color, topPosition, bottomPosition, percentsSum }, i) => {
			if (!color) return;

			const colorPosition = topPosition + (bottomPosition - topPosition) / 2;

			const isTooClose =
				i > 0 && Math.abs(colorsPositionsArr[i - 1]?.topPosition + correction - bottomPosition) < divHeight;

			correction = isTooClose ? correction + correctionForSmallFibers : 0;

			return (
				<div key={color} style={{ top: colorPosition - divHeight + correction }} className={color + ' percent'}>
					{percentsSum > 0 ? HelperService.FormatPercent(percentsSum, 2).replace(',', '.') : '< 0.01'}%
				</div>
			);
		});
	};

	useEffect(() => {
		const prepareSentSignals = HelperService.prepareSignalsCounterpartiesAml(
			details.CounterpartyDetails.Sent.SignalCounterparties || []
		);
		setSentSignals(prepareSentSignals);

		const prepareReceivedSignals = HelperService.prepareSignalsCounterpartiesAml(
			details.CounterpartyDetails.Received.SignalCounterparties || []
		);
		setReceivedSignals(prepareReceivedSignals);

		setUniqColorSignals([...new Set([...prepareSentSignals, ...prepareReceivedSignals].map(i => i.color))]);
	}, []);

	useEffect(() => {
		const observer = new ResizeObserver(([entry]) => {
			const widthWithoutPercents = entry.contentRect.width - widthBlockPercent - spaceBetweenSlides;

			setWidthDiagram(widthWithoutPercents - widthWithoutPercents * peekRationSlide);
		});

		ref.current && observer.observe(ref.current);
		return () => observer.disconnect();
	}, []);

	return (
		<div className='AMLInsightBlock'>
			<div className='AMLInsightBlock__summary'>
				<div className='AMLInsightBlock__summary-title'>{t('Received')}</div>
				<div className='AMLInsightBlock__summary-title'>{t('Sent')}</div>
			</div>
			<div className='AMLInsightBlock__summary'>
				<div className='AMLInsightBlock__summary-total'>
					${HelperService.FormatFiatWithoutCurrency(details.CounterpartyDetails.Received.TotalAmount)}
				</div>
				<div className='AMLInsightBlock__summary-total'>
					${HelperService.FormatFiatWithoutCurrency(details.CounterpartyDetails.Sent.TotalAmount)}
				</div>
			</div>

			{(details.CounterpartyDetails.Received.SignalCounterparties.length > 0 ||
				details.CounterpartyDetails.Sent.SignalCounterparties.length > 0) && (
				<>
					<div ref={ref} className='AMLInsightBlock__diagram'>
						<div id='AMLInsightBlock__for-tooltip' className={activeSlide === 1 ? 'mirrored' : ''}></div>
						<Swiper
							hashNavigation={{ watchState: true, replaceState: true }}
							modules={[HashNavigation, Pagination]}
							pagination={{
								dynamicBullets: false,
								dynamicMainBullets: 2,
								el: '.AMLInsightBlock__swiper-pagination',
								clickable: true,
							}}
							touchReleaseOnEdges={true}
							slidesPerView={1 + peekRationSlide}
							spaceBetween={spaceBetweenSlides}
							onSlideChange={swiper => setActiveSlide(swiper.activeIndex)}
							onTouchStart={() => setTooltipSwipe(prev => !prev)}
						>
							<SwiperSlide>
								<div className='AMLInsightBlock__slide'>
									<div className='AMLInsightBlock__percents'>{prepareDiagramPercentsBlocks(colorsPositionsLeft)}</div>
									<SankeyDiagram
										diagramArr={receivedSignals}
										prepareColorsPositions={positions => setColorsPositionsLeft(positions)}
										width={widthDiagram}
										closeTooltipTrigger={tooltipSwipe}
									/>
								</div>
							</SwiperSlide>
							<SwiperSlide>
								<div className='AMLInsightBlock__slide mirrored'>
									<SankeyDiagram
										diagramArr={sentSignals}
										prepareColorsPositions={positions => setColorsPositionsRight(positions)}
										isMirrored
										width={widthDiagram}
										closeTooltipTrigger={tooltipSwipe}
									/>
									<div className='AMLInsightBlock__percents'>{prepareDiagramPercentsBlocks(colorsPositionsRight)}</div>
								</div>
							</SwiperSlide>
							<div className='AMLInsightBlock__swiper-pagination'></div>
						</Swiper>
					</div>
					<div className='AMLInsightBlock__text'>{t('Risk level')}</div>
					<div className='AMLInsightBlock__risks'>
						{uniqColorSignals.map((i, index) => (
							<div className={classNames('AMLInsightBlock__risk', i)} key={index}>
								{i !== 'blue' && i !== 'violet' && <Icon type={Icons.AMLFlame} width={iconSize} height={iconSize} />}
								{t(colorParams[i].title)}
							</div>
						))}
					</div>
					<div className='AMLInsightBlock__text'>
						{t('How funds are categorized by risk level based on their origin and the destinations they were sent to.')}
					</div>
				</>
			)}
		</div>
	);
};

export default AMLInsightBlock;
